// src/components/pages/SalonSupportSection.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "../../utils/axiosConfig";
import { Mail, MessageCircle, ChevronLeft, Ticket, AlertCircle } from "lucide-react";

function buildWaLink(rawPhone = "", text = "") {
  let digits = (rawPhone || "").replace(/\D/g, "");
  digits = digits.replace(/^0+/, "").replace(/^15/, "");
  if (!/^54/.test(digits)) digits = `54${digits}`;   // ajusta si tu país no es AR
  const q = text ? `?text=${encodeURIComponent(text)}` : "";
  return `https://wa.me/${digits}${q}`;
}

export default function SalonSupportSection({ cliente, isAdmin }) {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(cliente || null);
  const [loading, setLoading] = useState(!cliente);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (cliente || !slug) return;
    let alive = true;
    setLoading(true);
    setErr("");
    axios
      .get(`/api/clients/${slug}`)
      .then(({ data }) => { if (alive) setData(data); })
      .catch(() => { if (alive) setErr("No pudimos cargar los datos de contacto."); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [slug, cliente]);

  const isSports = data?.organizationType === "sports";
  const email = data?.adminEmail || "";
  const phone = data?.contactPhone || "";
  const nombre = data?.nombre || (isSports ? "Tu complejo" : "Tu salón");

  const wa = useMemo(
    () => (phone ? buildWaLink(phone, `Hola ${nombre}, tengo una consulta sobre el ${isSports ? 'complejo' : 'salón'}.`) : ""),
    [phone, nombre, isSports]
  );

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary-orange/20 border-t-primary-orange rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!email && !phone) {
    return (
      <section className="py-20 px-6 text-center">
        <div className="max-w-xl mx-auto bg-white/5 border border-white/10 p-12 rounded-[40px]">
          <div className="w-16 h-16 bg-white/5 rounded-2xl flex items-center justify-center text-white/20 mx-auto mb-6">
            <AlertCircle size={32} />
          </div>
          <h2 className="text-3xl font-black mb-4">Ayuda no disponible</h2>
          <p className="text-white/40 font-medium mb-8">
            Este {isSports ? 'complejo' : 'salón'} todavía no configuró sus datos de contacto. Por favor, volvé a intentar más tarde.
          </p>
          {isAdmin && (
            <Link
              to={`/${slug}/profile`}
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-orange text-white rounded-2xl font-bold hover:scale-105 transition-all"
            >
              Configurar perfil
            </Link>
          )}
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 px-6 relative z-10">
      <div className="max-w-5xl mx-auto text-center">
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-full mb-8 animate-fade-in-up">
          <span className="flex h-2 w-2 rounded-full bg-primary-orange animate-pulse" />
          <span className="text-[10px] font-black uppercase tracking-[2px] text-white/60">
            Soporte del {isSports ? 'Complejo' : 'Salón'}
          </span>
        </div>

        <h2 className="text-4xl sm:text-6xl font-black tracking-tight mb-4 animate-fade-in-up">
          ¿Necesitás <span className="text-gradient">ayuda?</span>
        </h2>
        <p className="text-lg text-white/40 font-medium max-w-2xl mx-auto mb-16 animate-fade-in-up">
          Contactate con el equipo de <strong className="text-white">{nombre}</strong> para consultas sobre tus {isSports ? 'inscripciones' : 'tickets'} o el {isSports ? 'torneo' : 'evento'}.
        </p>

        <div className="grid sm:grid-cols-2 gap-8 animate-fade-in-up">
          {!!email && (
            <a
              href={`mailto:${email}?subject=${encodeURIComponent(`Consulta para ${nombre}`)}`}
              className="bg-glass p-10 rounded-[40px] shadow-2xl group hover:-translate-y-2 transition-all duration-500 shine-effect text-left flex flex-col"
            >
              <div className="mb-8 p-6 bg-white/5 rounded-3xl w-fit text-primary-orange group-hover:scale-110 transition-all duration-500">
                <Mail size={32} />
              </div>
              <h3 className="text-2xl font-black tracking-tight mb-2">Email del {isSports ? 'Complejo' : 'Salón'}</h3>
              <p className="text-white/40 font-medium leading-relaxed mb-6">
                Enviá tu consulta directamente a los organizadores por correo electrónico.
              </p>
              <div className="mt-auto flex items-center gap-2 text-primary-orange font-bold group-hover:gap-4 transition-all">
                {email} <ChevronLeft className="rotate-180" size={18} />
              </div>
            </a>
          )}

          {!!phone && (
            <a
              href={wa}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-glass p-10 rounded-[40px] shadow-2xl group hover:-translate-y-2 transition-all duration-500 shine-effect text-left flex flex-col"
            >
              <div className="mb-8 p-6 bg-white/5 rounded-3xl w-fit text-green-500 group-hover:scale-110 transition-all duration-500">
                <MessageCircle size={32} />
              </div>
              <h3 className="text-2xl font-black tracking-tight mb-2">WhatsApp {isSports ? 'Complejo' : 'Salón'}</h3>
              <p className="text-white/40 font-medium leading-relaxed mb-6">
                Comunicate de forma directa y rápida por WhatsApp con el {isSports ? 'complejo' : 'salón'}.
              </p>
              <div className="mt-auto flex items-center gap-2 text-green-500 font-bold group-hover:gap-4 transition-all">
                Abrir chat <ChevronLeft className="rotate-180" size={18} />
              </div>
            </a>
          )}
        </div>

        {isAdmin && (
          <div className="mt-12 p-6 bg-white/5 border border-white/5 rounded-3xl max-w-xl mx-auto">
            <p className="text-xs text-white/20 font-medium">
              Panel de Admin: Estos datos se configuran en la sección de <Link to={`/${slug}/profile`} className="text-primary-orange hover:underline font-bold">Perfil del {isSports ? 'complejo' : 'salón'}</Link>.
            </p>
          </div>
        )}
      </div>
    </section>
  );
}
